// src/services/seasonService.js
//
// Season creation outside the CreateSeason form. Currently one entry
// point: the auto-default season written at team-wizard finish
// (features/auto-default-season/).

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';
import { seasonAutoNameParts, buildDefaultSeasonPayload } from '@/utils/defaultSeason';

/**
 * Create the free open default season for a freshly wizard-created team.
 *
 * - Idempotent: no-ops (returns null) if the team already has ANY season,
 *   so a re-run finish can't duplicate (D8; residual race accepted per
 *   security review F3 — the Finish button's in-flight guard covers the
 *   realistic double-tap).
 * - Name comes from the coach's current UI language via the passed t()
 *   ("Fall 2026" / "Otoño 2026") and is stored as plain data, exactly
 *   like a hand-typed name.
 * - Throws on write failure — the CALLER decides failure is soft (A8).
 *
 * @returns {Promise<string|null>} new season id, or null if skipped.
 */
export async function createDefaultSeason({ teamId, uid, t }) {
  const existing = await getDocs(
    query(collection(db, 'seasons'), where('teamId', '==', teamId), limit(1))
  );
  if (!existing.empty) return null;

  const { key, year } = seasonAutoNameParts();
  const name = t(`createSeason.autoName.${key}`, { year });

  const ref = await addDoc(collection(db, 'seasons'), {
    ...buildDefaultSeasonPayload({ teamId, uid, name }),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // auto: true keeps activation analytics honest — an auto season must
  // not count as "coach created a season" (D9). Status is draft until
  // the coach's one-tap open (D10).
  track(ACTIVATION_EVENTS.SEASON_CREATED, {
    season_id: ref.id,
    team_id: teamId,
    status: 'draft',
    pricing_mode: 'flat',
    has_payment_plan: false,
    auto: true,
  });

  return ref.id;
}
